import { Button, Drawer, Input, InputNumber, Select, Switch } from "@arco-design/web-react";
import { useEffect, useRef, useState } from "react";

import { getCityOptions, getDistrictOptions, getProvinceOptions } from "../../../shared/region";
import {
  GeoOperationPanel,
  type GeoBusinessVerifyResult,
  type GeoOperationStatus,
} from "../../../shared/business-geo";
import { dicStayAreaLevelLabels, dicStayAreaLevelOptions } from "../config";
import { emptyDicStayAreaMutation } from "../runtime/useDicStayAreaRuntime";
import type { DicStayAreaItem, DicStayAreaMutation } from "../types";
import "./DicStayAreaPage.css";

type ModalState = { type: "create" } | { type: "edit"; stayArea: DicStayAreaItem };

type DicStayAreaMutationModalProps = {
  state: ModalState | null;
  onClose: () => void;
  errorMessage: string;
  geoResolving: boolean;
  geoStatus: GeoOperationStatus;
  geoResult?: GeoBusinessVerifyResult;
  onCreateStayArea: (payload: DicStayAreaMutation) => Promise<void>;
  onUpdateStayArea: (item: DicStayAreaItem, payload: DicStayAreaMutation) => Promise<void>;
  onResolveGeo: (payload: DicStayAreaMutation) => Promise<DicStayAreaMutation>;
  onClearError: () => void;
  onClearGeoFeedback: () => void;
};

/** 住宿点新增与编辑共用的右侧抽屉。 */
export function DicStayAreaMutationModal({
  state,
  onClose,
  errorMessage,
  geoResolving,
  geoStatus,
  geoResult,
  onCreateStayArea,
  onUpdateStayArea,
  onResolveGeo,
  onClearError,
  onClearGeoFeedback,
}: DicStayAreaMutationModalProps) {
  const drawerContentRef = useRef<HTMLDivElement | null>(null);
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [localErrorMessage, setLocalErrorMessage] = useState("");
  const [payload, setPayload] = useState<DicStayAreaMutation>(emptyDicStayAreaMutation);
  const displayedErrorMessage = localErrorMessage || errorMessage;
  const provinceOptions = withCurrentValueOption(getProvinceOptions(), payload.province).map(
    (province) => ({
      label: province,
      value: province,
    }),
  );
  const cityOptions = withCurrentValueOption(getCityOptions(payload.province), payload.city).map(
    (city) => ({
      label: city,
      value: city,
    }),
  );
  const districtOptions = withCurrentValueOption(
    getDistrictOptions(payload.province, payload.city),
    payload.district,
  ).map((district) => ({
    label: district,
    value: district,
  }));

  useEffect(() => {
    if (!state) {
      return;
    }
    onClearError();
    onClearGeoFeedback();
    setLocalErrorMessage("");
    if (state.type === "edit") {
      setPayload({
        stayAreaName: state.stayArea.stayAreaName,
        spellCode: state.stayArea.spellCode,
        areaLevel: state.stayArea.areaLevel,
        province: state.stayArea.province,
        city: state.stayArea.city,
        district: state.stayArea.district ?? "",
        township: state.stayArea.township ?? "",
        navigationAddress: state.stayArea.navigationAddress,
        lng: state.stayArea.lng,
        lat: state.stayArea.lat,
        isDisabled: state.stayArea.isDisabled,
        isAiCreated: state.stayArea.isAiCreated,
        remark: state.stayArea.remark,
      });
      return;
    }
    setPayload(emptyDicStayAreaMutation);
  }, [state]);

  /** 保存期间阻止重复提交；失败提示由 runtime 维护，保留表单供重试。 */
  async function handleOk() {
    if (!state || savingRef.current) {
      return;
    }
    if (!payload.stayAreaName.trim()) {
      setLocalErrorMessage("请填写住宿点名称。");
      return;
    }
    if (!payload.province.trim() || !payload.city.trim()) {
      setLocalErrorMessage("请选择省份和城市。");
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setLocalErrorMessage("");
    onClearError();
    try {
      if (state.type === "create") {
        await onCreateStayArea(payload);
      } else {
        await onUpdateStayArea(state.stayArea, payload);
      }
      onClose();
    } catch {
      // runtime 已写入错误提示，抽屉不关闭，也不丢弃本次填写内容。
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  /** 使用 GEO 识别回填表单。 */
  async function handleResolveGeo() {
    if (geoResolving || savingRef.current) {
      return;
    }
    try {
      setPayload(await onResolveGeo(payload));
    } catch {
      // runtime 已展示可重试状态；技术失败保留原表单。
    }
  }

  const levelOptions = dicStayAreaLevelOptions.some((option) => option.value === payload.areaLevel)
    ? dicStayAreaLevelOptions
    : [
        ...dicStayAreaLevelOptions,
        { value: payload.areaLevel, label: dicStayAreaLevelLabels[payload.areaLevel] ?? payload.areaLevel },
      ];

  return (
    <Drawer
      {...{
        role: "dialog",
        "aria-modal": true,
        "aria-label": state?.type === "edit" ? "编辑住宿点" : "新增住宿点",
      }}
      title={state?.type === "edit" ? "编辑住宿点" : "新增住宿点"}
      placement="right"
      width="min(720px, 100vw)"
      headerStyle={{ padding: "20px 24px" }}
      bodyStyle={{ padding: 24 }}
      visible={Boolean(state)}
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      maskClosable={!saving}
      escToExit={!saving}
      getChildrenPopupContainer={(node) => drawerContentRef.current ?? node.parentElement ?? document.body}
      onCancel={() => {
        if (savingRef.current) return;
        setLocalErrorMessage("");
        onClearError();
        onClearGeoFeedback();
        onClose();
      }}
      onOk={handleOk}
      okText="保存"
      cancelText="取消"
      unmountOnExit
    >
      <div
        className="dic-stay-area-drawer-form"
        ref={drawerContentRef}
      >
        {displayedErrorMessage ? (
          <div role="alert" className="dic-stay-area-error">
            {displayedErrorMessage}
          </div>
        ) : null}
        <section className="dic-stay-area-form-section" aria-label="基本信息">
          <h3>基本信息</h3>
          <div className="dic-stay-area-form-grid">
            <div className="dic-stay-area-field dic-stay-area-field--full">
              <span>住宿点名称</span>
              <Input
                aria-label="住宿点名称"
                placeholder="请输入住宿点名称"
                value={payload.stayAreaName}
                onChange={(stayAreaName) => setPayload({ ...payload, stayAreaName })}
              />
            </div>
            <div className="dic-stay-area-field">
              <span>层级</span>
              <Select
                aria-label="层级"
                value={payload.areaLevel}
                onChange={(areaLevel) => setPayload({ ...payload, areaLevel })}
              >
                {levelOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="dic-stay-area-field">
              <span>拼音码</span>
              <Input
                aria-label="拼音码"
                placeholder="保存时自动生成"
                value={payload.spellCode}
                onChange={(spellCode) => setPayload({ ...payload, spellCode })}
              />
            </div>
          </div>
        </section>
        <section className="dic-stay-area-form-section" aria-label="位置与导航">
          <div className="dic-stay-area-section-heading">
            <h3>位置与导航</h3>
            <Button type="outline" loading={geoResolving} disabled={geoResolving || saving} onClick={handleResolveGeo}>
              {geoResolving ? "识别中..." : "GEO识别"}
            </Button>
          </div>
          <p className="dic-stay-area-form-hint">填写住宿点名称和省份后，可辅助识别地址与坐标。</p>
          <div className="dic-stay-area-form-grid">
            <div className="dic-stay-area-field">
              <span>省份</span>
              <Select
                data-testid="stay-area-province-select"
                aria-label="省份"
                placeholder="请选择省份"
                options={provinceOptions}
                value={payload.province}
                onChange={(province) =>
                  setPayload({ ...payload, province, city: "", district: "", township: "" })
                }
              />
            </div>
            <div className="dic-stay-area-field">
              <span>城市</span>
              <Select
                data-testid="stay-area-city-select"
                aria-label="城市"
                disabled={!payload.province}
                placeholder="请选择城市"
                options={cityOptions}
                value={payload.city}
                onChange={(city) => setPayload({ ...payload, city, district: "", township: "" })}
              />
            </div>
            <div className="dic-stay-area-field">
              <span>区县</span>
              <Select
                data-testid="stay-area-district-select"
                aria-label="区县"
                disabled={!payload.city}
                placeholder="请选择区县"
                options={districtOptions}
                value={payload.district}
                onChange={(district) => setPayload({ ...payload, district, township: "" })}
              />
            </div>
            <div className="dic-stay-area-field">
              <span>镇/乡/街道</span>
              <Input
                aria-label="镇/乡/街道"
                placeholder="选填，请输入镇、乡或街道"
                value={payload.township}
                onChange={(township) => setPayload({ ...payload, township })}
              />
            </div>
            <div className="dic-stay-area-field dic-stay-area-field--full">
              <span>导航地址</span>
              <Input
                aria-label="导航地址"
                placeholder="填写导航地址，或通过 GEO 识别回填"
                value={payload.navigationAddress}
                onChange={(navigationAddress) => setPayload({ ...payload, navigationAddress })}
              />
            </div>
            <div className="dic-stay-area-field">
              <span>经度</span>
              <InputNumber
                aria-label="经度"
                placeholder="GEO 识别回填或手动填写"
                style={{ width: "100%" }}
                hideControl
                value={payload.lng}
                onChange={(lng) => setPayload({ ...payload, lng })}
              />
            </div>
            <div className="dic-stay-area-field">
              <span>纬度</span>
              <InputNumber
                aria-label="纬度"
                placeholder="GEO 识别回填或手动填写"
                style={{ width: "100%" }}
                hideControl
                value={payload.lat}
                onChange={(lat) => setPayload({ ...payload, lat })}
              />
            </div>
          </div>
          {state && (geoStatus !== "idle" || geoResult) ? (
            <GeoOperationPanel status={geoStatus} result={geoResult} onRetry={() => void handleResolveGeo()} />
          ) : null}
        </section>
        <section className="dic-stay-area-form-section" aria-label="其他设置">
          <h3>其他设置</h3>
          <div className="dic-stay-area-form-grid">
            <div className="dic-stay-area-switch-field">
              <span>停用</span>
              <Switch
                aria-label="停用"
                checked={payload.isDisabled}
                onChange={(isDisabled) => setPayload({ ...payload, isDisabled })}
              />
            </div>
            <div className="dic-stay-area-switch-field">
              <span>AI新增</span>
              <Switch
                aria-label="AI新增"
                checked={payload.isAiCreated}
                onChange={(isAiCreated) => setPayload({ ...payload, isAiCreated })}
              />
            </div>
            <div className="dic-stay-area-field dic-stay-area-field--full">
              <span>备注</span>
              <Input.TextArea
                aria-label="备注"
                placeholder="选填，补充住宿点说明"
                autoSize={{ minRows: 3, maxRows: 5 }}
                value={payload.remark}
                onChange={(remark) => setPayload({ ...payload, remark })}
              />
            </div>
          </div>
        </section>
      </div>
    </Drawer>
  );
}

/** 确保当前值即使不在静态选项中，也能被 Select 正常显示。 */
function withCurrentValueOption(options: string[], currentValue: string) {
  const normalizedCurrentValue = currentValue.trim();
  if (!normalizedCurrentValue || options.includes(normalizedCurrentValue)) {
    return options;
  }
  return [normalizedCurrentValue, ...options];
}
